import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { Sentry } from "@/lib/sentry";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AgentProvider } from "@/contexts/AgentContext";
import { AllocationLogProvider } from "@/contexts/AllocationLogContext";
import { AllocationWeightsProvider } from "@/contexts/AllocationWeightsContext";
import { MockAgentsProvider } from "@/contexts/MockAgentsContext";
import { PricingRulesProvider } from "@/contexts/PricingRulesContext";
import { SystemControlProvider } from "@/contexts/SystemControlContext";
import { LeadStoreProvider } from "@/contexts/LeadStoreContext";
import { ContractsProvider } from "@/contexts/ContractsContext";
import { ReconciliationProvider } from "@/contexts/ReconciliationContext";
import { DeliveryControlProvider } from "@/contexts/DeliveryControlContext";
import { ClientLedgerProvider } from "@/contexts/ClientLedgerContext";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { AgentRoute } from "@/components/AgentRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Leads from "./pages/dashboard/Leads";
import LeadDetail from "./pages/dashboard/LeadDetail";
import Preferences from "./pages/dashboard/Preferences";
import DeliverySettings from "./pages/dashboard/DeliverySettings";
import Wallet from "./pages/dashboard/Wallet";
import Performance from "./pages/dashboard/Performance";
import Messages from "./pages/dashboard/Messages";
import Settings from "./pages/dashboard/Settings";
import NotFound from "./pages/NotFound";
// Admin Pages
import AdminOverview from "./pages/admin/AdminOverview";
import AdminLogs from "./pages/admin/AdminLogs";
import AdminAgents from "./pages/admin/AdminAgents";
import AdminSystem from "./pages/admin/AdminSystem";
import AdminPricing from "./pages/admin/AdminPricing";
import AdminRoutingLab from "./pages/admin/AdminRoutingLab";
import AdminRoutingInspector from "./pages/admin/AdminRoutingInspector";
import AdminContracts from "./pages/admin/AdminContracts";
import AdminDeliveryLedger from "./pages/admin/AdminDeliveryLedger";
import AdminLeadVault from "./pages/admin/AdminLeadVault";
import AdminAccountManagers from "./pages/admin/AdminAccountManagers";
import AdminClientHealth from "./pages/admin/AdminClientHealth";
import AdminMediaBuying from "./pages/admin/AdminMediaBuying";
// AM Pages
import { AMProtectedRoute } from "@/components/AMProtectedRoute";
import AMDashboard from "./pages/am/AMDashboard";
import AMInbox from "./pages/am/AMInbox";
import AMClientDetail from "./pages/am/AMClientDetail";
import AMTasks from "./pages/am/AMTasks";
import AMSettings from "./pages/am/AMSettings";

/**
 * Normalize Supabase PostgrestError objects ({ code, details, hint, message })
 * into real Error instances so Sentry captures a useful message + stack trace.
 */
function toSentryError(error: unknown): { err: Error; extra?: Record<string, unknown> } {
  if (error instanceof Error) return { err: error };

  if (
    error != null &&
    typeof error === "object" &&
    "message" in error &&
    "code" in error
  ) {
    const pgErr = error as { code: string; message: string; details?: string; hint?: string };
    const err = new Error(`[Supabase] ${pgErr.message} (code: ${pgErr.code})`);
    err.name = "PostgrestError";
    return { err, extra: { code: pgErr.code, details: pgErr.details, hint: pgErr.hint } };
  }

  return { err: new Error(String(error)) };
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const { err, extra } = toSentryError(error);
      Sentry.captureException(err, {
        tags: { source: "react-query", queryKey: String(query.queryKey[0] ?? "") },
        ...(extra && { contexts: { supabase: extra } }),
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      const { err, extra } = toSentryError(error);
      Sentry.captureException(err, {
        tags: { source: "react-query-mutation" },
        ...(extra && { contexts: { supabase: extra } }),
      });
    },
  }),
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AgentProvider>
            <AllocationLogProvider>
              <AllocationWeightsProvider>
                <MockAgentsProvider>
                  <PricingRulesProvider>
                    <SystemControlProvider>
                      <LeadStoreProvider>
                        <ContractsProvider>
                          <ReconciliationProvider>
                            <DeliveryControlProvider>
                              <ClientLedgerProvider>
                                <Routes>
                                  <Route path="/" element={<Index />} />
                                  <Route path="/login" element={<Login />} />
                                  <Route path="/signup" element={<Signup />} />
                                  <Route path="/forgot-password" element={<ForgotPassword />} />
                                  <Route path="/reset-password" element={<ResetPassword />} />
                                  <Route path="/auth/callback" element={<AuthCallback />} />
                                  <Route path="/payment-success" element={<PaymentSuccess />} />
                                  <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                                  <Route path="/onboarding" element={<ProtectedRoute><ErrorBoundary><Onboarding /></ErrorBoundary></ProtectedRoute>} />
                                  <Route path="/dashboard" element={<AgentRoute><ErrorBoundary><Dashboard /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/leads" element={<AgentRoute><ErrorBoundary><Leads /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/leads/:id" element={<AgentRoute><ErrorBoundary><LeadDetail /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/preferences" element={<AgentRoute><ErrorBoundary><Preferences /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/delivery-settings" element={<AgentRoute><ErrorBoundary><DeliverySettings /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/wallet" element={<AgentRoute><ErrorBoundary><Wallet /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/performance" element={<AgentRoute><ErrorBoundary><Performance /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/messages" element={<AgentRoute><ErrorBoundary><Messages /></ErrorBoundary></AgentRoute>} />
                                  <Route path="/dashboard/settings" element={<AgentRoute><ErrorBoundary><Settings /></ErrorBoundary></AgentRoute>} />
                                  {/* AM Routes */}
                                  <Route path="/am" element={<AMProtectedRoute><ErrorBoundary><AMDashboard /></ErrorBoundary></AMProtectedRoute>} />
                                  <Route path="/am/clients/:agentId" element={<AMProtectedRoute><ErrorBoundary><AMClientDetail /></ErrorBoundary></AMProtectedRoute>} />
                                  <Route path="/am/inbox" element={<AMProtectedRoute><ErrorBoundary><AMInbox /></ErrorBoundary></AMProtectedRoute>} />
                                  <Route path="/am/tasks" element={<AMProtectedRoute><ErrorBoundary><AMTasks /></ErrorBoundary></AMProtectedRoute>} />
                                  <Route path="/am/settings" element={<AMProtectedRoute><ErrorBoundary><AMSettings /></ErrorBoundary></AMProtectedRoute>} />
                                  {/* Admin Routes */}
                                  <Route path="/admin" element={<AdminRoute allowedRoles={["admin"]}><AdminOverview /></AdminRoute>} />
                                  <Route path="/admin/routing-lab" element={<AdminRoute allowedRoles={["admin"]}><AdminRoutingLab /></AdminRoute>} />
                                  <Route path="/admin/routing-inspector" element={<AdminRoute allowedRoles={["admin"]}><AdminRoutingInspector /></AdminRoute>} />
                                  <Route path="/admin/contracts" element={<AdminRoute><AdminContracts /></AdminRoute>} />
                                  <Route path="/admin/delivery-ledger" element={<AdminRoute allowedRoles={["admin"]}><AdminDeliveryLedger /></AdminRoute>} />
                                  <Route path="/admin/lead-vault" element={<AdminRoute allowedRoles={["admin"]}><AdminLeadVault /></AdminRoute>} />
                                  <Route path="/admin/account-managers" element={<AdminRoute allowedRoles={["admin"]}><AdminAccountManagers /></AdminRoute>} />
                                  <Route path="/admin/logs" element={<AdminRoute allowedRoles={["admin"]}><AdminLogs /></AdminRoute>} />
                                  <Route path="/admin/agents" element={<AdminRoute allowedRoles={["admin"]}><AdminAgents /></AdminRoute>} />
                                  <Route path="/admin/system" element={<AdminRoute allowedRoles={["admin"]}><AdminSystem /></AdminRoute>} />
                                  <Route path="/admin/pricing" element={<AdminRoute allowedRoles={["admin"]}><AdminPricing /></AdminRoute>} />
                                  <Route path="/admin/media-buying" element={<AdminRoute allowedRoles={["admin"]}><AdminMediaBuying /></AdminRoute>} />
                                  <Route path="/admin/client-health" element={<AdminRoute allowedRoles={["admin"]}><AdminClientHealth /></AdminRoute>} />
                                  <Route path="*" element={<NotFound />} />
                                </Routes>
                              </ClientLedgerProvider>
                            </DeliveryControlProvider>
                          </ReconciliationProvider>
                        </ContractsProvider>
                      </LeadStoreProvider>
                    </SystemControlProvider>
                  </PricingRulesProvider>
                </MockAgentsProvider>
              </AllocationWeightsProvider>
            </AllocationLogProvider>
          </AgentProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
